import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Card, Text, Button,
  Spinner, Checkbox, FormItem, Input, Select, Textarea, Snackbar,
} from '@vkontakte/vkui';
import { Icon24Add, Icon24Write, Icon24Delete } from '@vkontakte/icons';
import { useWeddingStore } from '../../stores/wedding';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';
import { ModalRoot, ModalPageHeader } from '@vkontakte/vkui';
import { DragHandle } from '../../ui/DragHandle';
import type { ScheduleRecord, WeddingEvent } from '../../types';

type Modal = 'form' | 'confirm-delete' | null;

interface FormState {
  event_id: string;
  name: string;
  date: string;
  time: string;
  note: string;
  address: string;
}

const emptyForm: FormState = {
  event_id: '',
  name: '',
  date: '',
  time: '',
  note: '',
  address: '',
};

/** Local calendar day YYYY-MM-DD (not UTC — avoids split blocks near midnight). */
function dayKey(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toDateInput(iso?: string): string {
  if (!iso) return '';
  return dayKey(iso);
}

function toTimeInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toTimeString().slice(0, 5);
}

function combineDateTime(date: string, time: string): string | undefined {
  if (!date) return undefined;
  const t = time || '00:00';
  return new Date(`${date}T${t}:00`).toISOString();
}

function formatTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function formatDay(iso?: string): string {
  if (!iso) return 'Без даты';
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const label = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', weekday: 'short' });
  if (same(d, today)) return `Сегодня · ${label}`;
  if (same(d, tomorrow)) return `Завтра · ${label}`;
  return label;
}

export default function SchedulePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventFilter = searchParams.get('event') || '';
  const {
    currentWedding, schedule, events, isLoading,
    loadSchedule, loadEvents, createSchedule, updateSchedule,
    deleteSchedule, toggleSchedule,
  } = useWeddingStore();

  const [openToken, setOpenToken] = useState(0);
  const [modal, setModal] = useState<Modal>(null);
  const [editing, setEditing] = useState<ScheduleRecord | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ScheduleRecord | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  useEffect(() => {
    if (!currentWedding) return;
    loadSchedule().catch(() => setSnack('Не удалось загрузить расписание'));
    loadEvents().catch(() => {});
  }, [currentWedding?.id]);

  const activeEvent = useMemo(
    () => events.find((e) => e.id === eventFilter) || null,
    [events, eventFilter],
  );

  const filteredSchedule = useMemo(() => {
    if (!eventFilter) return schedule;
    return schedule.filter((r) => r.event_id === eventFilter);
  }, [schedule, eventFilter]);

  const eventMap = useMemo(() => {
    const m = new Map<string, WeddingEvent>();
    events.forEach((e) => m.set(e.id, e));
    return m;
  }, [events]);

  /** Group by local calendar day; sort by time inside the day. */
  const groups = useMemo(() => {
    const byDay = new Map<string, ScheduleRecord[]>();
    [...filteredSchedule].forEach((r) => {
      const key = dayKey(r.date);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(r);
    });
    for (const [, items] of byDay) {
      items.sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0;
        const tb = b.date ? new Date(b.date).getTime() : 0;
        return ta - tb;
      });
    }
    return [...byDay.entries()].sort(([a], [b]) => {
      // '' (no date) last
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    });
  }, [filteredSchedule]);

  function openCreate() {
    const ev = events.find((e) => e.id === eventFilter) || events[0];
    setEditing(null);
    const evDate = ev?.date_start ? toDateInput(ev.date_start) : (currentWedding?.wedding_date || '');
    const evTime = ev?.date_start ? toTimeInput(ev.date_start) : '10:00';
    setForm({
      ...emptyForm,
      event_id: eventFilter || ev?.id || '',
      date: evDate,
      time: evTime,
      address: ev?.address || '',
    });
    setOpenToken((t) => t + 1);
    setModal('form');
  }

  function onEventChange(eventId: string) {
    const ev = events.find((e) => e.id === eventId);
    setForm((f) => ({
      ...f,
      event_id: eventId,
      // prefill from event if fields empty
      date: f.date || (ev?.date_start ? toDateInput(ev.date_start) : f.date),
      time: f.time || (ev?.date_start ? toTimeInput(ev.date_start) : f.time),
      address: f.address || ev?.address || '',
    }));
  }

  function openEdit(r: ScheduleRecord) {
    setEditing(r);
    setForm({
      event_id: r.event_id,
      name: r.name,
      date: toDateInput(r.date),
      time: toTimeInput(r.date),
      note: r.note || '',
      address: r.address || '',
    });
    setOpenToken((t) => t + 1);
    setModal('form');
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSnack('Укажите название');
      return;
    }
    if (!form.event_id) {
      setSnack('Выберите мероприятие');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        event_id: form.event_id,
        name: form.name.trim(),
        note: form.note.trim() || undefined,
        date: combineDateTime(form.date, form.time),
        address: form.address.trim() || undefined,
      };
      if (editing) {
        await updateSchedule(editing.id, payload);
        setSnack('Сохранено');
      } else {
        await createSchedule(payload);
        setSnack('Добавлено в расписание');
      }
      setModal(null);
      setEditing(null);
    } catch {
      setSnack('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await deleteSchedule(confirmDelete.id);
      setSnack('Удалено');
      setConfirmDelete(null);
      setModal(null);
    } catch {
      setSnack('Не удалось удалить');
    }
  }

  if (!currentWedding) {
    return (
      <Panel>
        <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>Расписание</PanelHeader>
        <Div><Text>Сначала выберите свадьбу</Text></Div>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelHeader
        before={
          <PanelHeaderBack
            onClick={() => navigate(eventFilter ? '/events' : '/')}
          />
        }
      >
        {activeEvent ? `Расписание · ${activeEvent.name}` : 'Расписание'}
      </PanelHeader>

      {isLoading && filteredSchedule.length === 0 ? (
        <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
      ) : filteredSchedule.length === 0 ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 40 }}>
            <Text style={{ marginBottom: 12, color: 'var(--vkui--color_text_secondary)' }}>
              {activeEvent
                ? `Пока нет пунктов расписания для «${activeEvent.name}».`
                : 'Пока нет записей. Добавьте первый пункт дня свадьбы.'}
            </Text>
            <Button size="l" mode="primary" stretched before={<Icon24Add />} onClick={openCreate}>
              Добавить запись
            </Button>
          </Div>
        </Group>
      ) : (
        groups.map(([dayKey, items]) => (
          <Group
            key={dayKey || 'none'}
            header={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '8px 12px 4px',
                }}
              >
                <Text style={{ fontWeight: 600 }}>{formatDay(items[0]?.date)}</Text>
                <Button
                  size="s"
                  mode="secondary"
                  before={<Icon24Add />}
                  onClick={openCreate}
                >
                  Запись
                </Button>
              </div>
            }
          >
            {items.map((r) => (
              <Card key={r.id} style={{ margin: '8px 12px', padding: 12 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 48, paddingTop: 2 }}>
                    <Text style={{ fontSize: 13, fontWeight: 600 }}>{formatTime(r.date)}</Text>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Checkbox
                      checked={r.is_complete}
                      onChange={(e) => void toggleSchedule(r.id, e.currentTarget.checked)}
                    >
                      <span style={{ textDecoration: r.is_complete ? 'line-through' : 'none' }}>
                        {r.name}
                      </span>
                    </Checkbox>
                    {eventMap.get(r.event_id)?.name && (
                      <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginTop: 2 }}>
                        {eventMap.get(r.event_id)?.name}
                      </Text>
                    )}
                    {r.address && (
                      <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginTop: 2 }}>
                        {r.address}
                      </Text>
                    )}
                    {r.note && (
                      <Text style={{ fontSize: 12, marginTop: 4 }}>{r.note}</Text>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <Button size="s" mode="tertiary" onClick={() => openEdit(r)}><Icon24Write /></Button>
                    <Button
                      size="s"
                      mode="tertiary"
                      onClick={() => {
                        setConfirmDelete(r);
                        setOpenToken((t) => t + 1);
                        setModal('confirm-delete');
                      }}
                    >
                      <Icon24Delete />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </Group>
        ))
      )}

      <ModalRoot
        activeModal={
          modal === 'form' ? sheetId('schedule-form', openToken)
            : modal === 'confirm-delete' ? sheetId('schedule-delete', openToken) : null
        }
        onClose={() => { setModal(null); setEditing(null); setConfirmDelete(null); }}
      >
        <ResizableModalPage
          openToken={openToken}
          id="schedule-form"
          onClose={() => { setModal(null); setEditing(null); }}
          settlingHeight={80}
        >
          <ModalPageHeader>{editing ? 'Редактировать' : 'Новая запись'}</ModalPageHeader>
          <DragHandle />
          <Group>
            <FormItem top="Мероприятие *">
              <Select
                value={form.event_id}
                onChange={(e) => onEventChange(e.currentTarget.value)}
                options={events.map((ev) => ({ label: ev.name, value: ev.id }))}
                placeholder="Выберите"
              />
              <Text style={{ marginTop: 4, fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
                Дата, время и место можно взять из мероприятия
              </Text>
            </FormItem>
            <FormItem top="Название *">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Например: Сборы невесты"
              />
            </FormItem>
            <FormItem top="Дата">
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </FormItem>
            <FormItem top="Время">
              <Input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
              />
            </FormItem>
            <FormItem top="Место">
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Адрес"
              />
            </FormItem>
            <FormItem top="Заметка">
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Необязательно"
              />
            </FormItem>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Button size="l" mode="primary" stretched disabled={saving} onClick={handleSave}>
                {saving ? 'Сохраняем…' : 'Сохранить'}
              </Button>
              <Button size="l" mode="secondary" onClick={() => { setModal(null); setEditing(null); }}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage
          openToken={openToken}
          id="schedule-delete"
          onClose={() => { setModal(null); setConfirmDelete(null); }}
          settlingHeight={80}
        >
          <ModalPageHeader>Удалить запись?</ModalPageHeader>
          <Group>
            <Div>
              <Text style={{ marginBottom: 12 }}>{confirmDelete?.name}</Text>
              <Button size="l" mode="primary" stretched style={{ marginBottom: 8 }} onClick={handleDelete}>
                Удалить
              </Button>
              <Button size="l" mode="secondary" stretched onClick={() => { setModal(null); setConfirmDelete(null); }}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>
      </ModalRoot>

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)}>{snack}</Snackbar>
      )}
    </Panel>
  );
}
